import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';
import { motion } from 'framer-motion';
import { FileText, ShieldCheck, Clock, HardDrive, Activity } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';

// Mock Data for fallback
const CHART_DATA = [
    { name: 'Mon', uv: 4000 }, { name: 'Tue', uv: 3000 }, { name: 'Wed', uv: 2000 },
    { name: 'Thu', uv: 2780 }, { name: 'Fri', uv: 1890 }, { name: 'Sat', uv: 2390 },
    { name: 'Sun', uv: 3490 },
];
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const Dashboard = () => {
    const { user, token } = useAuth();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const res = await axios.get('http://localhost:4000/api/analytics/dashboard', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setStats(res.data.data);
            } catch (err) {
                console.error("Failed to fetch dashboard stats", err);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, [token]);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1 }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: { y: 0, opacity: 1 }
    };

    return (
        <Layout>
            <motion.div
                className="space-y-8"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                {loading ? (
                     <div className="flex h-[50vh] items-center justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                     </div>
                ) : (
                <>
                {/* Header */}
                <motion.div variants={itemVariants} className="flex justify-between items-end">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
                            Welcome back, <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-teal-400">{user?.email?.split('@')[0]}</span> 👋
                        </h1>
                        <p className="mt-2 text-gray-500 dark:text-gray-400">
                            Here's what's happening with your documents today.
                        </p>
                    </div>
                </motion.div>

                {/* Stats Grid */}
                <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <StatCard
                        title="Total Storage"
                        value={stats ? `${(stats.storage.totalBytes / 1024 / 1024).toFixed(2)} MB` : '...'}
                        icon={<HardDrive className="w-6 h-6 text-white" />}
                        color="bg-gradient-to-br from-blue-500 to-blue-600"
                    />
                    <StatCard
                        title="Total Files"
                        value={stats ? stats.storage.fileCount : '...'}
                        icon={<FileText className="w-6 h-6 text-white" />}
                        color="bg-gradient-to-br from-purple-500 to-purple-600"
                    />
                    <StatCard
                        title="Expiring Soon"
                        value={stats ? "2" : "0"} // Mock for now
                        icon={<Clock className="w-6 h-6 text-white" />}
                        color="bg-gradient-to-br from-amber-500 to-orange-500"
                    />
                    <StatCard
                        title="Security Status"
                        value="Secure"
                        icon={<ShieldCheck className="w-6 h-6 text-white" />}
                        color="bg-gradient-to-br from-emerald-500 to-teal-500"
                    />
                </motion.div>

                {/* Charts Section */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Activity Chart */}
                    <motion.div variants={itemVariants} className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-100 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                <Activity className="w-5 h-5 text-blue-500" />
                                Storage Trends
                            </h3>
                        </div>
                        <div className="h-[300px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={stats?.storageTrend || CHART_DATA}>
                                    <defs>
                                        <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#374151" opacity={0.1} />
                                    <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF' }} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF' }} />
                                    <Tooltip
                                        contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px', color: '#fff' }}
                                    />
                                    <Area type="monotone" dataKey="size" stroke="#3B82F6" strokeWidth={3} fillOpacity={1} fill="url(#colorUv)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </motion.div>

                    {/* Categories Donut */}
                    <motion.div variants={itemVariants} className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-100 dark:border-gray-700">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Distribution</h3>
                        <div className="h-[300px] relative">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={stats?.categories || []}
                                        innerRadius={60}
                                        outerRadius={80}
                                        paddingAngle={5}
                                        dataKey="count"
                                    >
                                        {(stats?.categories || []).map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                            {/* Center Text */}
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="text-center">
                                    <span className="block text-2xl font-bold text-gray-900 dark:text-white">{stats?.storage.fileCount || 0}</span>
                                    <span className="text-xs text-gray-500">Files</span>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Recent Activity */}
                <motion.div variants={itemVariants} className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-100 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Activity</h3>
                    <div className="space-y-4">
                        {stats?.recentActivity?.map((log, i) => (
                            <div key={i} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                                <div className="flex items-center gap-4">
                                    <div className={`p-2 rounded-lg ${log.action === 'UPLOAD' ? 'bg-green-100 text-green-600' :
                                            log.action === 'DELETE' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'
                                        }`}>
                                        <FileText className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900 dark:text-white">{log.details}</p>
                                        <p className="text-xs text-gray-500">{new Date(log.created_at).toLocaleString()}</p>
                                    </div>
                                </div>
                                <span className="text-xs font-mono bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded">
                                    {log.action}
                                </span>
                            </div>
                        ))}
                    </div>
                </motion.div>
                </>
                )}
            </motion.div>
        </Layout>
    );
};

const StatCard = ({ title, value, icon, color }) => (
    <div className={`relative overflow-hidden rounded-2xl p-6 shadow-lg ${color} text-white`}>
        <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                    {icon}
                </div>
                <span className="text-xs font-medium bg-white/20 px-2 py-1 rounded backdrop-blur-md">
                    +2.5%
                </span>
            </div>
            <h3 className="text-3xl font-bold mb-1">{value}</h3>
            <p className="text-blue-100 text-sm font-medium">{title}</p>
        </div>
        {/* Decorative Circles */}
        <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute -left-4 -bottom-4 w-20 h-20 bg-black/5 rounded-full blur-xl" />
    </div>
);

export default Dashboard;
